import * as React from 'react';
import {useEffect, useState} from 'react';
import {Appearance, Image, Platform, Text, View} from 'react-native';
import Config from 'react-native-config';
import FastImage from 'react-native-fast-image';
import {GestureHandlerRootView} from 'react-native-gesture-handler';
import {QueryClientProvider} from 'react-query';
import '~i18n';
import Navigation from './navigation/Navigation';
import {useLanguage} from './navigation/SettingsStack/zustand';
// import * as Sentry from '@sentry/react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';

import {useTranslation} from 'react-i18next';
import {StatusBar} from 'react-native';
import {KeyboardProvider} from 'react-native-keyboard-controller';
import {NotifierWrapper} from 'react-native-notifier';
import {getFcmToken} from '~Utils/fcmHelper';
import {ThemeProvider} from '~contexts/ThemeContext';

// Conditionally import Stripe providers
let StripeProvider: any;

if (Platform.OS !== 'web') {
  try {
    StripeProvider = require('@stripe/stripe-react-native').StripeProvider;
  } catch (e) {
    StripeProvider = React.Fragment;
  }
} else {
  StripeProvider = React.Fragment;
}
import {NotificationProvider} from '~contexts/NotificationContext';
import {SubscriptionProvider} from '~contexts/SubscriptionContext';
import {initializeGlobalErrorHandler} from '~Utils/globalErrorHandler';
import './../../Utils/fetchIntercept';
import {startPerformanceLogging} from '~Utils/performance/performanceMonitor';
import PrefetchService from '~services/performance/PrefetchService';
import { createOptimizedQueryClient } from '~Utils/performance';
import { appStartup, timing } from '~Utils/debugLogger';
import PageTitleManager from '~components/PageTitleManager';

// Conditionally import native-only modules
let SplashScreen: any = null;
let OneSignal: any = null;
let crashlytics: any = null;
let PermissionsAndroid: any = null;

if (Platform.OS !== 'web') {
  SplashScreen = require('react-native-splash-screen');
  OneSignal = require('react-native-onesignal').default;
  crashlytics = require('@react-native-firebase/crashlytics').default;
  PermissionsAndroid = require('react-native').PermissionsAndroid;
} else {
  // Import web stubs
  SplashScreen = require('~services/web/splashScreenStub').default;
  OneSignal = require('~services/web/oneSignalStub').default;
  crashlytics = require('~services/web/crashlyticsStub').default;
}
import { STRIPE_PUBLISH_KEY } from '@env';
import { useTheme } from '@react-navigation/native';

// Sentry.init({
//   dsn: 'https://3dab74c1cdbf4eca9c11632df1867388@o4505561113755648.ingest.sentry.io/4505561115131904',
//   debug: false,
//   release: DeviceInfo.getApplicationName() + '@' + DeviceInfo.getVersion() + '(' + DeviceInfo.getBuildNumber() + ')',
//   tracesSampleRate: 1,
// });
const queryClient = createOptimizedQueryClient();

// Initialize performance monitoring and prefetching
const prefetchService = new PrefetchService(queryClient);

// Start performance logging in development
if (__DEV__) {
  startPerformanceLogging(30000); // Log every 30 seconds in dev
}

const isAndroid = Platform.OS === 'android';
const isWeb = Platform.OS === 'web';

export default function App() {  
  const { colors} = useTheme();
  // OneSignal Initialization with error handling and delays for physical devices
  // Skip on web platform

  React.useEffect(() => {
    if (isWeb) {
      console.log('🌐 [WEB] Skipping OneSignal initialization on web platform');
      return;
    }

    const initializeOneSignal = async () => {
      try {
        console.log('🔔 Initializing OneSignal...');
        OneSignal.setLogLevel(6, 0);

        if (Config.ONE_SIGNAL_APP_ID) {
          OneSignal.setAppId(Config.ONE_SIGNAL_APP_ID + '');

          // Add delay for physical device stability
          setTimeout(() => {
            try {
              // Request permissions for both iOS and Android
              if (!isAndroid) {
                OneSignal.promptForPushNotificationsWithUserResponse();
              } else {
                // For Android, ensure push is enabled by default
                OneSignal.disablePush(false);
              }
              console.log('✅ OneSignal initialized successfully');
            } catch (permissionError) {
              console.error('❌ OneSignal permission error:', permissionError);
            }
          }, 2000); // 2 second delay for physical devices
        } else {
          console.warn('⚠️ OneSignal App ID not found');
        }
      } catch (error) {
        console.error('❌ OneSignal initialization error:', error);
      }
    };

    initializeOneSignal();
  }, []);
  //END OneSignal Init Code

  console.log(`🚀 [DEBUG] App component started rendering - Platform: ${Platform.OS}`);
  try {
    appStartup(`App component started rendering on ${Platform.OS}`);
  } catch (error) {
    console.error('❌ [DEBUG] Error with appStartup logger:', error);
  }
  const appStartTime = Date.now();

  // Initialize global error handler for view hierarchy errors
  React.useEffect(() => {
    initializeGlobalErrorHandler();
  }, []);

  // Initialize Firebase Crashlytics safely (skip on web)
  React.useEffect(() => {
    if (isWeb) {
      console.log('🌐 [WEB] Skipping Crashlytics initialization on web platform');
      return;
    }

    const initializeCrashlytics = async () => {
      try {
        // Add delay to prevent early initialization crashes
        setTimeout(async () => {
          try {
            // Check if crashlytics is available and enabled
            const isCrashlyticsCollectionEnabled = crashlytics().isCrashlyticsCollectionEnabled;
            console.log('🔥 Crashlytics collection enabled:', isCrashlyticsCollectionEnabled);

            if (!isCrashlyticsCollectionEnabled) {
              // Enable crashlytics collection manually (since auto-collection is disabled)
              await crashlytics().setCrashlyticsCollectionEnabled(true);
              console.log('✅ Crashlytics collection enabled manually');
            }

            // Set user identifier for crash reports
            crashlytics().setUserId('anonymous-user');

            // Log that crashlytics is ready
            crashlytics().log('Crashlytics initialized successfully');
            console.log('✅ Firebase Crashlytics initialized safely');
          } catch (crashlyticsError) {
            console.error('❌ Crashlytics initialization error:', crashlyticsError);
          }
        }, 3000); // 3 second delay to ensure app is fully loaded
      } catch (error) {
        console.error('❌ Error setting up Crashlytics initialization:', error);
      }
    };

    initializeCrashlytics();
  }, []);

  const {i18n} = useTranslation();
  const {setLanguage, language} = useLanguage();

  const [darkTheme, setDarkTheme] = useState(false);

  useEffect(() => {
    // Subscribe to theme changes
    const themeListener = Appearance.addChangeListener(({colorScheme}) => {
      setDarkTheme(colorScheme === 'dark');
    });

    // Cleanup listener
    return () => themeListener.remove();
  }, []);

  useEffect(() => {
    // Skip FCM token setup on web
    if (isWeb) {
      console.log('🌐 [WEB] Skipping FCM token setup on web platform');
      return;
    }

    const fetchAndSaveFcmToken = async () => {
      try {
        const deviceFcmToken = await getFcmToken();
        console.log('📱 FCM Token obtained:', deviceFcmToken ? 'Success' : 'Failed');

        if (deviceFcmToken) {
          // Store FCM token locally for later use when user is authenticated
          // Commenting out Firestore update to prevent crashes on physical devices
          console.log('✅ FCM token obtained but not updating Firestore to prevent crashes');

          // TODO: Update FCM token after user authentication in a safer way
          // await FirebaseChatsService.updateUserFcmToken(deviceFcmToken);
        }
      } catch (error) {
        console.error('❌ Error fetching FCM token:', error);
      }
    };

    fetchAndSaveFcmToken();
  }, []);

  //Method for handling notifications received while app in foreground
  // Handle notifications received while the app is in the foreground
  // useEffect(() => {
  //   handleForegroundNotification();
  // }, []);


  //Method for handling notifications opened
  useEffect(() => {
    // Skip image preloading on web - resolveAssetSource doesn't exist on web
    if (isWeb) {
      return;
    }

    appStartup('Starting image preloading');
    const imagePreloadStart = Date.now();

    const images = [
      require('~assets/images/personalImage.jpg'),
      require('~assets/images/businessImage.jpg'),
      require('~assets/images/SignInImage.png'),
      require('~assets/images/eventPhotoPlaceholder.png'),
      require('~assets/images/mainSplashImage.jpg'),
    ];
    const uris = images.map(image => ({
      uri: Image.resolveAssetSource(image).uri,
    }));

    FastImage.preload(uris);
    timing('Image preloading initiated', imagePreloadStart);
  }, []);

  useEffect(() => {
    appStartup('Setting up splash screen hide timer');
    const timeout = setTimeout(() => {
      appStartup('Hiding splash screen');
      if (!isWeb) {
        SplashScreen.hide();
      }
      timing('Total app startup time', appStartTime);
    }, 200);
    return () => clearTimeout(timeout);
  }, [appStartTime]);

  React.useEffect(() => {
    // Skip Android permissions on web
    if (Platform.OS === 'android' && !isWeb) {
      PermissionsAndroid.request(PermissionsAndroid.PERMISSIONS.WRITE_CALENDAR, {
        title: 'Calendar Permission',
        message: 'App needs access to your calendar to create events.',
        buttonPositive: 'OK',
      });
    }
  }, []);

  useEffect(() => {
    // Skip for web
    if (isWeb) {
      return;
    }

    AsyncStorage.getItem('language-storage').then((item: any) => {
      const locale = JSON.parse(item).state?.language;
      i18n.changeLanguage(locale);
      setLanguage(locale);
    });
  }, [language]);

  // OneSignal subscription management (skip on web)
  useEffect(() => {
    if (isWeb) {
      return;
    }

    const initializeOneSignal = async () => {
      try {
        // Check if user was previously opted out and re-enable if needed
        const deviceState = await OneSignal.getDeviceState();
        console.log('OneSignal Device State:', deviceState);

        // If push is disabled, check if we should re-enable it
        if (deviceState?.isPushDisabled) {
          // Check if user has notifications enabled in settings
          const notificationSettings = await AsyncStorage.getItem('notifications-storage');
          if (notificationSettings) {
            const settings = JSON.parse(notificationSettings);
            if (settings.state?.isNotificationsEnabled !== false) {
              // Re-enable push notifications
              OneSignal.disablePush(false);
              console.log('✅ OneSignal push notifications re-enabled');
            }
          } else {
            // Default to enabled for new users
            OneSignal.disablePush(false);
            console.log('✅ OneSignal push notifications enabled for new user');
          }
        }
      } catch (error) {
        console.error('❌ OneSignal initialization error:', error);
      }
    };

    initializeOneSignal();
  }, []);

  // Initialize background prefetching
  useEffect(() => {
    const initializePrefetching = async () => {
      try {
        // Start prefetching popular content after app loads
        await prefetchService.prefetchPopularEvents({priority: 'medium', delay: 3000});
        await prefetchService.prefetchSubcategories({priority: 'low', delay: 5000});

        console.log('🚀 Background prefetching initialized');
      } catch (error) {
        console.warn('Failed to initialize prefetching:', error);
      }
    };

    // Delay initialization to not block app startup
    const timer = setTimeout(initializePrefetching, 2000);
    return () => clearTimeout(timer);
  }, []);

  // Note: Appearance.setColorScheme is deprecated and causes UI thread errors
  // Theme management is now handled by ThemeContext
  // useEffect(() => {
  //   Appearance.setColorScheme('light');
  // }, []);
  // useEffect(() => {
  //   if (userLocation?.coords?.latitude && userLocation?.coords?.longitude && userLocation?.locationName) {
  //     setCurrentPositionState({
  //       latitude: userLocation.coords.latitude,
  //       longitude: userLocation.coords.longitude,
  //       address: userLocation.locationName,
  //     });
  //   }
  // }, [userLocation?.locationName, userLocation?.coords.longitude, userLocation?.coords.latitude]);

  const [renderError, setRenderError] = useState<string | null>(null);
  const [appReady, setAppReady] = useState(false);

  // Test basic setup on mount
  React.useEffect(() => {
    console.log('🧪 [WEB] App mounted, testing basic setup');
    console.log('🧪 [WEB] Platform.OS:', Platform.OS);
    console.log('🧪 [WEB] Config loaded:', !!Config);
    console.log('🧪 [WEB] QueryClient created:', !!queryClient);
    setAppReady(true);
  }, []);

  console.log('� [DEBUG] App render cycle - appReady:', appReady, 'renderError:', renderError);

  // If there's an error, show it immediately
  if (renderError) {
    console.error('🔴 [DEBUG] Showing error screen:', renderError);
    return (
      <View style={{flex: 1, justifyContent: 'center', alignItems: 'center', backgroundColor: '#fff'}}>
        <Text style={{color: '#f00', fontSize: 16}}>Error: {renderError}</Text>
      </View>
    );
  }

  try {
    console.log('🚀 [DEBUG] App component rendering main content');
    
    // Render the full Navigation stack
    console.log('🚀 [DEBUG] Rendering Navigation component');
    
    // Base app content
    const baseContent = (
      <GestureHandlerRootView style={{flex: 1, width: '100%', height: '100%'}}>
        <NotifierWrapper>
          <Navigation />
        </NotifierWrapper>
      </GestureHandlerRootView>
    );

    // Wrap with StripeProvider on non-web
    const contentWithStripeProvider = Platform.OS !== 'web' ? (
      <StripeProvider publishableKey={STRIPE_PUBLISH_KEY || ''}>
        {baseContent}
      </StripeProvider>
    ) : (
      baseContent
    );

    // On web, wrap with max-width container logic
    const appContent = (
      <ThemeProvider>
        <KeyboardProvider>
          <QueryClientProvider client={queryClient}>
            <NotificationProvider>
              <SubscriptionProvider>
                {contentWithStripeProvider}
              </SubscriptionProvider>
            </NotificationProvider>
          </QueryClientProvider>
        </KeyboardProvider>
      </ThemeProvider>
    );
    
    // Wrap with PageTitleManager for web to ensure title stays "Pyxi"
    const wrappedContent = isWeb ? (
      <PageTitleManager>
        {appContent}
      </PageTitleManager>
    ) : (
      appContent
    );
    
    // Wrap with web-specific max-width container if on web
    if (isWeb) {
      return (
        <View style={{
          flex: 1,
          width: '100%',
          height: '100%',
          justifyContent: 'center',
          alignItems: 'center',
          backgroundColor: '#f5f5f5',
        }}>
          <View style={{
            flex: 1,
            width: '100%',
            height: '100%',
            maxWidth: 480,
            backgroundColor: '#fff',
          }}>
            {wrappedContent}
          </View>
        </View>
      );
    }
    
    return wrappedContent;
  } catch (error: any) {
    console.error('🔴 [DEBUG] Caught error in render:', error?.message);
    console.error('🔴 [DEBUG] Stack:', error?.stack);
    setRenderError(error?.message || 'Unknown error during render');
    return (
      <View style={{flex: 1, justifyContent: 'center', alignItems: 'center', backgroundColor: '#fff'}}>
        <Text style={{color: '#f00', fontSize: 16}}>Error: {error?.message || 'Unknown'}</Text>
      </View>
    );
  }
}
